// Dedications.tsx
import Button from '../Button'
import { useQuery } from 'react-query'
import { Dispatch, SetStateAction, useState, useEffect } from 'react'
import React from 'react'
import { GetDedications } from '@/services/api/personalize/GetDedications'
import { toast } from 'react-hot-toast'
import CustomToast from '../CustomToast'
import { useRouter } from 'next/router'
import ImageUploader from './ImageUploader'
import { DedicationEdit } from '@/utils/svgIcons'
import { useTranslation } from 'next-i18next'

interface Props {
  product: any
  setInputDedication: Dispatch<SetStateAction<string>>
  inputDedication: any
  SubmitChange?: any
  close?: any
  setOption: Dispatch<SetStateAction<number | undefined>>
  gender: string
  setDedicationModal?: any
  updateProductData?: any
  handleSave?: any
  isEdit?: any
  markOptionCompleted?: any
  nameFromPersonalize?: any
  croppedImage?: any
  setCroppedImage?: any
  imageSelected?: any
  setImageSelected?: any
  updateDedicationImage?: any
  effectiveImageSelected?:any
  croppedFile?: any
  setCroppedFile?:any
  imageInEdit?:any
}

export default function Dedications({
  croppedImage,
  setCroppedImage,
  imageSelected,
  setImageSelected,
  product,
  setInputDedication,
  inputDedication,
  SubmitChange,
  gender,
  setOption,
  setDedicationModal,
  close = undefined,
  updateProductData,
  handleSave,
  isEdit,
  markOptionCompleted,
  nameFromPersonalize,
  updateDedicationImage,
  effectiveImageSelected,
  croppedFile,
  setCroppedFile,
  imageInEdit
}: Props) {
  const { data, isLoading } = useQuery(['dedications', gender], GetDedications)
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)
  const limit = 400
  const [tempDedication, setTempDedication] = React.useState(inputDedication || '')
  const [selectedId, setSelectedId] = useState<number | 'custom' | null>(null)
  const { locale } = useRouter();

  // English fallback dedications (when backend does NOT provide English)
  const englishDedications: Record<number, { name_en: string; dedication_en: string }> = {
    3: {
      name_en: "Dedication - Girl",
      dedication_en:
        "Dear {basmti},\nYou are a wonderful girl! You can do anything you set your mind to. Never stop chasing your dreams, because even if you stumble along the way, we'll be there to lift you up and cheer you on.\nWith all our love,\nYour parents\n",
    },
    4: {
      name_en: "Dedication - Girl",
      dedication_en:
        "{basmti}, we love you so much and wish you success and progress.\nFrom Mom and Dad.",
    },
    2: {
      name_en: "Dedication - Boy",
      dedication_en:
        "Dear {basmti},\nI hope you enjoy this book. It was made just for you.\nYou bring color to our lives and everyone around you.\nMay you have many adventures and wonderful experiences on your life journey. Be brave, be curious, and trust yourself.",
    },
    5: {
      name_en: "Dedication - Boy",
      dedication_en:
        "{basmti}, you are the most precious thing in our life.\nWe love you so much and wish you continued success.\nWe will always be with you supporting you to achieve your dreams.\nFrom Mom and Dad.\nWe love you very much.",
    },
    6: {
      name_en: "Dedication - Boy",
      dedication_en:
        "Dear {basmti}\nIn this story, you are its hero, defying odds and achieving dreams. Every adventure you embark on within its pages reflects the greatness of your spirit and the courage that fills my life with pride and joy.\nYou are a shining star in the sky of my imagination, and through this story, I want you to always remember how special and valued you are.\nEnjoy every moment of your adventure, and may your life be as full of excitement and happiness as it is in these pages.\nWith love\n",
    },
    7: {
      name_en: "Dedication - Girl",
      dedication_en:
        "My dearest {basmti}\nIn this story, you are its heroine, defying challenges and achieving dreams. Every adventure you embark on within its pages reflects the greatness of your spirit and your courage, which fills my life with pride and joy.\nYou are a star shining brightly in the sky of my imagination, and through this story, I want you to always remember how special you are.\nEnjoy every moment of your adventure, and may your life be as full of excitement and happiness as it is in these pages.\nWith all my love\n",
    },
  };

  const setDedicationIfWithinLimit = (text: string) => {
    if (selectedId !== 'custom' || text.length <= limit) {
      setInputDedication(text);
    }
  };

  const checkReplaceName =
    isEdit === '1'
      ? product?.name || ''
      : (nameFromPersonalize !== undefined && nameFromPersonalize !== '')
        ? nameFromPersonalize
        : locale === 'ar' ? product?.replace_name?.ar || '' : product?.replace_name?.en || '';

  React.useEffect(() => {
    setTempDedication(inputDedication || '');
  }, [inputDedication]);

  useEffect(() => {
    if (data && selectedId != null && selectedId !== 'custom') {
      const selectedDedication = data.find((d: any) => d.id === selectedId);
      if (selectedDedication) {
        const rawText =
          locale === "ar"
            ? selectedDedication.dedication
            : englishDedications[selectedDedication.id]?.dedication_en ?? selectedDedication.dedication;
        
        const newText = rawText.replace('{basmti}', checkReplaceName);
        setTempDedication(newText);
        setDedicationIfWithinLimit(newText);
      }
    }
  }, [checkReplaceName, data, selectedId, locale]);

  // Initialize selected dedication when data loads or inputDedication changes
  useEffect(() => {
    if (data && inputDedication) {
      const matchedDedication = data.find((dedication: any) => {
        const rawText =
          locale === "ar"
            ? dedication.dedication
            : englishDedications[dedication.id]?.dedication_en ?? dedication.dedication;

        const fullText = rawText.replace("{basmti}", checkReplaceName);
        return fullText === inputDedication;
      });

      if (matchedDedication) {
        setSelectedId(matchedDedication.id);
      } else if (inputDedication) {
        setSelectedId('custom');
      }
    }
  }, [data, inputDedication, checkReplaceName, locale]);

  // FIXED: Safe word count calculation
  React.useEffect(() => {
    if (tempDedication) {
      // Safe split - handle undefined/null
      const words = (tempDedication || '')
        .trim()
        .split(/\s+/)
        .filter((word: string) => word.length > 0);
      setWordCount(words.length);
      setCharCount(tempDedication.length);
    } else {
      setWordCount(0);
      setCharCount(0);
    }
  }, [tempDedication]);

  const UpdateDedication = (id: number) => {
    const selected = data.filter((x: any) => x.id == id)[0];
    if (!selected) return;
    
    const rawText =
      locale === "ar"
        ? selected.dedication
        : englishDedications[selected.id]?.dedication_en ?? selected.dedication;

    const newText = rawText.replace("{basmti}", checkReplaceName);

    setTempDedication(newText)
    setDedicationIfWithinLimit(newText);
  }

  const {t} = useTranslation('personalizeModal');

  return (
    <div className="personalization-content dedication flex flex-col items-center w-full h-full" id='personalize-dedications'>
      {/* Header with close button - NOT fixed, will scroll with content */}
      <div className='w-full items-center justify-between p-6 pb-4 bg-white border-b border-gray-200 hidden lg:flex' dir='ltr'>
        <div className='flex items-start justify-center gap-2 flex-1'>
          <DedicationEdit/>
          <div className='text-left'>
            <h2 className='text-lg font-semibold'>{t('dedicationTextHeading')}</h2>
            <h4 className='text-sm font-light text-gray-600'>{t('dedicationTextSubHeading')}</h4>
          </div>
        </div>
        <button 
          className="bg-[#93A3B03D] text-xl p-2 rounded-md flex-shrink-0 ml-4" 
          onClick={() => {
            setDedicationModal(false);
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.23742 7.00023L13.7439 1.88765C14.0857 1.57026 14.0857 1.05566 13.7439 0.738291C13.4021 0.420895 12.8479 0.420895 12.5061 0.738291L7.00022 5.8514L1.49438 0.738291C1.15257 0.420895 0.598385 0.420895 0.256602 0.738291C-0.0851811 1.05569 -0.0852084 1.57028 0.256602 1.88765L5.76302 7.00023L0.256602 12.1128C-0.0852084 12.4302 -0.0852084 12.9448 0.256602 13.2622C0.598412 13.5796 1.15259 13.5796 1.49438 13.2622L7.00022 8.14905L12.5061 13.2622C12.8479 13.5796 13.4021 13.5796 13.7438 13.2622C14.0857 12.9448 14.0857 12.4302 13.7438 12.1128L8.23742 7.00023Z" fill="black"/>
          </svg>
        </button>
      </div>
      
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto w-full lg:p-6 scrollbar-hide" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Dedication options */}
        <div className="dedications-options bg-white flex flex-col items-center w-full gap-6 p-2 lg:p-0">
          {isLoading ? 
            (
            <div className='w-full px-4 py-8 bg-[#E8EADE] flex items-center justify-center gap-4'>
              <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <h2 className="text-lg font-semibold leading-none">{t('loadingPreDedications')}</h2>
            </div>
            )
            : 
            (
            <div className="bg-[#93A3B033] py-3 px-4 w-full rounded-md border flex flex-col gap-2">
              {data?.length != 0 ? (
                <ul className="space-y-3">
                {data?.map((dedication: any, index: number) => {
                  const isSelectedOption = selectedId === dedication.id;
                  const rawText =
                    locale === "ar"
                      ? dedication.dedication
                      : englishDedications[dedication.id]?.dedication_en ?? dedication.dedication;

                  const fullText = rawText.replace("{basmti}", checkReplaceName);

                  return (
                    <li
                      key={dedication.id}
                      className="flex flex-col gap-2 justify-between"
                    >
                      <div
                        className="rounded-md flex flex-row-reverse items-center justify-end gap-3 p-3 bg-white border border-gray-200 hover:border-primary transition-colors"
                        id='dedication-option'
                        onClick={() => {
                          setSelectedId(dedication.id);
                          setTempDedication(fullText);
                          setInputDedication(fullText);
                          setDedicationIfWithinLimit(fullText);
                        }}
                      >
                        <label htmlFor={`dedication-${dedication.id}`} className={`text-sm text-primary font-medium flex-1 ${locale === 'ar' ? 'text-right': 'text-left'} cursor-pointer`}>
                          {
                            locale === "ar"
                              ? dedication.name
                              : englishDedications[dedication.id]?.name_en ?? dedication.name
                          }
                        </label>
                        <label htmlFor={`dedication-${dedication.id}`} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="dedication"
                            id={`dedication-${dedication.id}`}
                            checked={isSelectedOption}
                            onChange={() => {}}
                            className="hidden peer"
                          />
                          <div className={`w-5 h-5 rounded-full ${isSelectedOption ? 'bg-[#00214D] border-2 border-[#00214D]' : 'bg-white border-2 border-gray-300'} flex items-center justify-center transition-all`}>
                            {isSelectedOption && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none">
                                <path
                                  d="M1 5.19922L3.70368 8.14736C4.1159 8.59686 4.83101 8.57584 5.21611 8.10291L11 1"
                                  stroke="#FFFFFF"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            )}
                          </div>
                        </label>
                      </div>

                      {isSelectedOption && (
                        <div className="mt-2">
                          <textarea
                            rows={6}
                            dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                            id='dedication-text-editor'
                            value={tempDedication}
                            onChange={(e) => {
                              const value = e.target.value;
                              setTempDedication(value);

                              if (selectedId === 'custom') {
                                if (value.length <= limit) {
                                  setInputDedication(value);
                                }
                              } else {
                                setInputDedication(value);
                              }
                            }}
                            className="border border-[#93A3B03D] w-full font-medium placeholder:font-light placeholder:opacity-70 p-4 text-sm focus:ring-2 transition duration-200 focus:ring-primary focus:border-transparent rounded-lg"
                            placeholder={`${t('enterDedicationPlaceholder')}`}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              ): (
                <p className='text-primary text-sm font-medium text-center py-4'>{t('noPreDedications')}</p>
              )}
            </div>
            )
          }
          
          {/* Custom dedication option */}
          <div className="flex flex-col gap-2 bg-white justify-between w-full">
            <div
              className="flex flex-row-reverse items-center justify-end gap-3 border-2 border-[#B0BAC8] rounded-md px-4 py-3 cursor-pointer hover:border-primary transition-colors"
              onClick={() => {
                setSelectedId('custom');
                setTempDedication(inputDedication || '');
                setInputDedication(inputDedication || '');
              }}
            >
              <label htmlFor="dedication-custom" className={`text-sm text-primary font-medium flex-1 ${locale === 'ar' ? 'text-right' : 'text-left'} cursor-pointer`}>
                {t('customDedicationText')}
              </label>
              <label htmlFor="dedication-custom" className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="dedication"
                  id="dedication-custom"
                  checked={selectedId === 'custom'}
                  onChange={() => {}}
                  className="hidden peer"
                />
                <div className={`w-5 h-5 rounded-full ${selectedId === 'custom' ? 'bg-[#00214D] border-2 border-[#00214D]' : 'bg-white border-2 border-gray-300'} flex items-center justify-center transition-all`}>
                  {selectedId === 'custom' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5.19922L3.70368 8.14736C4.1159 8.59686 4.83101 8.57584 5.21611 8.10291L11 1"
                        stroke="#FFFFFF"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
              </label>
            </div>

            {selectedId === 'custom' && (
              <div className="mt-4">
                <textarea
                  rows={6}
                  dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}
                  value={tempDedication}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTempDedication(value);

                    if (value.length <= limit) {
                      setInputDedication(value);
                    }
                  }}
                  className="border border-[#93A3B03D] w-full font-medium placeholder:font-light placeholder:opacity-70 p-4 text-sm focus:ring-2 transition duration-200 focus:ring-primary focus:border-transparent rounded-lg"
                  placeholder={`${t('enterDedicationPlaceholder')}`}
                />
                <p
                  className={`text mt-2 ${locale === 'ar' ? 'text-right': 'text-left'} text-sm font-medium ${
                    limit - charCount < 0 ? 'text-red-600' : 'text-gray-500'
                  }`}>
                  {t('wordsLimit')} {limit - charCount}
                </p>
              </div>
            )}
          </div>
          
          {/* Image uploader */}
          <div className="w-full mt-6">
            <ImageUploader
              croppedImage={croppedImage}
              setCroppedImage={setCroppedImage}
              imageSelected={imageSelected}
              effectiveImageSelected={effectiveImageSelected}
              setImageSelected={setImageSelected}
              uuid={product?.uuid}
              product={product}
              croppedFile={croppedFile}
              setCroppedFile={setCroppedFile}
              aiStory={false}
              isDedicationStep={true}
            />
          </div>
          
          {/* Continue button at the bottom of scrollable content */}
          <div className="w-full mt-8 pb-6 hidden lg:block">
            <Button
              type="button"
              bg="primary"
              id="dedications-continue-button"
              onClick={() => {
                // Only apply limit check if custom dedication is selected
                if (selectedId === 'custom' && limit - charCount < 0) {
                  toast.custom((tEl) => (
                    <CustomToast
                      t={tEl}
                      message={`${t('longDedicationError')}`}
                      type="error"
                    />
                  ));
                  return;
                }

                if (tempDedication.trim() === '') {
                  toast.custom((tEl) => (
                    <CustomToast t={tEl} message={`${t('enterDedicationToContinue')}`} type="error" />
                  ));
                  return;
                }

                if (imageSelected === 'yes' && (!croppedImage || croppedImage.trim() === '')) {
                  toast.custom((tEl) => (
                    <CustomToast t={tEl} message={`${t('pictureSelectionError')}`} type="error" />
                  ));
                  return;
                }

                const finalDedication = tempDedication;

                // if (finalDedication.length > limit) {
                //   toast.custom((tEl) => (
                //     <CustomToast
                //       t={tEl}
                //       message={`${t('longDedicationError')}`}
                //       type="error"
                //     />
                //   ));
                //   return;
                // }

                setInputDedication(finalDedication);
                updateDedicationImage(croppedImage);
                updateProductData({ dedication: finalDedication });
                markOptionCompleted('dedication', true);
                handleSave();
                setDedicationModal(false);
              }}
              classes={['w-full', 'py-3', 'text-lg']}
            >
              {`${t('continue')}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}